import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
import 'react-range-slider-input/dist/style.css';
import RangeSlider, { RangeSliderProps } from '@/components/functional/RangeInput/RangeSlider.tsx';
import type { InputEvent } from 'react-range-slider-input';

type RangeInputBaseProps = Pick<InputProps, 'color'> & {
  label?: string;
  error?: string;
  wrapperClassName?: string;
} & Omit<RangeSliderProps, 'onInput' | 'value' | 'rangeSlideDisabled' | 'thumbsDisabled'>;

type RangeInputSingleProps = RangeInputBaseProps & {
  single: true;
  onChange?: (value: number) => void;
  value: number;
};

type RangeInputDoubleProps = RangeInputBaseProps & {
  single?: false;
  onChange?: (value: InputEvent) => void;
  value: InputEvent;
};

type RangeInputProps = RangeInputSingleProps | RangeInputDoubleProps;

function RangeInput(props: RangeInputProps) {
  const { label, error, wrapperClassName, color, disabled, value, ...rest } = props;

  return (
    <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
      {label ? (
        <label className="label">
          <span className="label-text">{label}</span>
        </label>
      ) : null}
      <RangeSlider
        className={cn({
          'range--primary': color === 'primary' && !disabled,
          'range--secondary': color === 'secondary' && !disabled,
          'range--accent': color === 'accent' && !disabled,
          'range--info': color === 'info' && !disabled,
          'range--success': color === 'success' && !disabled,
          'range--warning': color === 'warning' && !disabled,
          'range--error': (error || color === 'error') && !disabled,
          'range--disabled': disabled,
          'single-thumb': !!props.single,
        })}
        disabled={disabled}
        rangeSlideDisabled={props.single}
        thumbsDisabled={props.single ? [true, false] : undefined}
        onInput={(e) =>
          props.single
            ? props.onChange
              ? props.onChange(e[1])
              : undefined
            : props.onChange
              ? props.onChange(e)
              : undefined
        }
        value={props.single ? [0, value as number] : (value as InputEvent)}
        {...rest}
      />
      {error ? (
        <label className="label">
          <span className="label-text-alt text-error">{error}</span>
        </label>
      ) : null}
    </div>
  );
}

export default RangeInput;
